import { useState } from "react";
import API from "../../api/api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toolbarOptions } from "../editorToolbar";
import DashboardLayout from "../dashboard/DashboardLayout";

export default function CreateArticle() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  // SPORTS FIELDS
  const [sport, setSport] = useState("");
  const [category, setCategory] = useState("");
  const [region, setRegion] = useState("global");

  const [tags, setTags] = useState("");
  const [featuredImage, setFeaturedImage] = useState("");
  const [imageCredit, setImageCredit] = useState(""); // ✅ IMAGE CREDIT
  const [seoTitle, setSeoTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [userId, setUserId] = useState("");
  const [isLive, setIsLive] = useState(false);

  const modules = { toolbar: toolbarOptions };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "list",
    "bullet",
    "align",
    "blockquote",
    "link",
    "image",
    "video",
  ];

  /* ================= PREVIEW ================= */
  const preview = () => {
    if (!title || !content) {
      alert("Title and content are required for preview");
      return;
    }

    const previewData = {
      title,
      content,
      sport,
      category,
      region,
      tags: tags ? tags.split(",").map((t) => t.trim()) : [],
      images: featuredImage,
      image_credit: imageCredit,
      seo_title: seoTitle || title,
      meta_description: metaDescription,
      created_at: new Date().toISOString(),
    };

    localStorage.setItem("article_preview", JSON.stringify(previewData));
    window.open("/article/preview", "_blank");
  };

  /* ================= PUBLISH ================= */
  const submit = async () => {
    if (!title || !content || !sport || !category || !userId) {
      alert("Please fill all required fields");
      return;
    }

    try {
      const payload = {
        title,
        content,
        sport,
        category,
        region,
        tags: tags ? tags.split(",").map((t) => t.trim()) : [],
        images: featuredImage || "",
        image_credit: imageCredit || "",
        seo_title: seoTitle || title,
        meta_description: metaDescription || "",
        user_id: Number(userId),
        is_live: isLive,
      };

      await API.post("/api/articles/", payload);
      alert("Sports article published successfully 🏆");
    } catch (err) {
      console.error(err);
      alert("Failed to publish article");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-6 py-10 bg-gray-50">

        {/* HEADER */}
        <div className="mb-8 border-l-4 border-red-600 pl-4">
          <h1 className="text-4xl font-extrabold text-gray-900">
            Create Sports Article
          </h1>
          <p className="text-gray-600 mt-1">
            Publish breaking news, match updates & analysis
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ================= EDITOR ================= */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow border border-red-200">
            <div className="p-6 border-b border-red-200">
              <input
                className="w-full p-3 text-lg font-semibold border rounded"
                placeholder="Match headline / Sports article title…"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="p-6">
              <ReactQuill
                value={content}
                onChange={setContent}
                modules={modules}
                formats={formats}
                theme="snow"
                className="min-h-[380px]"
              />
            </div>
          </div>

          {/* ================= RIGHT PANEL ================= */}
          <div className="space-y-6 sticky top-24 h-fit">

            {/* ACTIONS */}
            <div className="bg-white rounded-xl shadow border border-red-300 p-5 space-y-3">
              <button
                onClick={preview}
                className="w-full py-3 rounded-lg font-semibold
                           text-red-600 border border-red-500
                           hover:bg-red-50 transition"
              >
                👁 Preview Article
              </button>

              <button
                onClick={submit}
                className="w-full py-3 rounded-lg font-semibold
                           text-white bg-red-600 hover:bg-red-700 transition"
              >
                🚀 Publish Article
              </button>
            </div>

            {/* ARTICLE DETAILS */}
            <div className="bg-white rounded-xl shadow border border-red-200 p-5 space-y-4">
              <h3 className="font-semibold text-lg text-red-600">
                Article Details
              </h3>

              <input
                className="w-full border rounded p-2"
                placeholder="Sport (e.g. Football, Cricket)"
                value={sport.replace(/-/g, " ")}
                onChange={(e) =>
                  setSport(
                    e.target.value
                      .trim()
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                  )
                }
              />

              <select
                className="w-full border rounded p-2"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Select Category</option>
                <option value="trending">Trending</option>
                <option value="breaking-news">Breaking News</option>
                <option value="analysis">Analysis</option>
                <option value="highlights">Highlights</option>
              </select>

              <select
                className="w-full border rounded p-2"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              >
                <option value="global">Global</option>
                <option value="india">India</option>
                <option value="uk">UK</option>
                <option value="usa">USA</option>
              </select>

              <input
                className="w-full border rounded p-2"
                placeholder="Tags (football, live, final)"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />

              <input
                className="w-full border rounded p-2"
                placeholder="Featured Image URL"
                value={featuredImage}
                onChange={(e) => setFeaturedImage(e.target.value)}
              />

              {/* ✅ IMAGE CREDIT INPUT (VISIBLE) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image Credit
                </label>
                <input
                  className="w-full border rounded p-2"
                  placeholder="e.g. Miami Hurricanes Football / X"
                  value={imageCredit}
                  onChange={(e) => setImageCredit(e.target.value)}
                />
              </div>

              <input
                className="w-full border rounded p-2"
                placeholder="Author User ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              />
            </div>

            {/* STATUS */}
            <div className="bg-white rounded-xl shadow border border-red-200 p-5">
              <h3 className="font-semibold text-lg text-red-600 mb-3">
                Publish Status
              </h3>

              <div className="flex items-center justify-between">
                <p className="font-medium">
                  {isLive ? "Live on Website" : "Draft Mode"}
                </p>

                <button
                  onClick={() => setIsLive(!isLive)}
                  className={`relative h-7 w-14 rounded-full transition
                    ${isLive ? "bg-red-600" : "bg-gray-300"}`}
                >
                  <span
                    className={`absolute top-1 h-5 w-5 bg-white rounded-full transition
                      ${isLive ? "left-8" : "left-1"}`}
                  />
                </button>
              </div>
            </div>

            {/* SEO */}
            <div className="bg-white rounded-xl shadow border border-red-200 p-5 space-y-3">
              <h3 className="font-semibold text-lg text-red-600">
                SEO Settings
              </h3>

              <input
                className="w-full border rounded p-2"
                placeholder="SEO Title"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
              />

              <textarea
                className="w-full border rounded p-2"
                rows={3}
                placeholder="Meta description for Google"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
              />
            </div>

          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
